import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom';
import Navigation from './components/Navigation';
import Home from './pages/Home';
import Galleries from './pages/Galleries';
import GalleryPage from './pages/Gallery';
import Login from './pages/Login';
import Admin from './pages/Admin';
import AdminImageEdit from './pages/AdminImageEdit';
import AdminGallery from './pages/AdminGallery';
import AdminSiteSettings from './pages/AdminSiteSettings';
import { fetchCurrentSiteSettings, getAuthToken } from './services/api';
import {
  getCurrentSiteKey,
  SiteKey,
  subscribeToSiteBrandingUpdate,
  subscribeToSiteChange,
} from './services/site';

type SiteTheme = {
  primaryColor?: string;
  backgroundColor?: string;
  textColor?: string;
  logoMaxHeight?: number;
  borderRadius?: number;
};

const setCssVar = (name: string, value?: string | number | null) => {
  const root = document.documentElement;
  if (value === undefined || value === null || value === '') {
    root.style.removeProperty(name);
    return;
  }
  root.style.setProperty(name, String(value));
};

const applySiteTheme = (theme?: SiteTheme | null) => {
  setCssVar('--site-primary-color', theme?.primaryColor || null);
  setCssVar('--site-bg-color', theme?.backgroundColor || null);
  setCssVar('--site-text-color', theme?.textColor || null);
  setCssVar('--site-logo-max-height', theme?.logoMaxHeight ? `${theme.logoMaxHeight}px` : null);
  setCssVar('--site-border-radius', theme?.borderRadius !== undefined ? `${theme.borderRadius}px` : null);
};

const App: React.FC = () => {
  const [currentGalleryTitle, setCurrentGalleryTitle] = useState<string | null>(null);
  const [siteKey, setSiteKey] = useState<SiteKey>(getCurrentSiteKey());

  useEffect(() => {
    const handleRouteChange = () => {
      if (!window.location.pathname.startsWith('/gallery/')) {
        setCurrentGalleryTitle(null);
      }
    };

    handleRouteChange();
    window.addEventListener('popstate', handleRouteChange);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);

  useEffect(() => subscribeToSiteChange(setSiteKey), []);

  useEffect(() => {
    return subscribeToSiteBrandingUpdate((updatedSiteKey) => {
      if (updatedSiteKey === siteKey) {
        fetchCurrentSiteSettings(siteKey)
          .then((settings) => applySiteTheme(settings?.theme || null))
          .catch(() => applySiteTheme(null));
      }
    });
  }, [siteKey]);

  useEffect(() => {
    let active = true;
    fetchCurrentSiteSettings(siteKey)
      .then((settings) => {
        if (!active) return;
        applySiteTheme(settings?.theme || null);
      })
      .catch(() => {
        if (!active) return;
        applySiteTheme(null);
      });

    return () => {
      active = false;
    };
  }, [siteKey]);

  return (
    <Router>
      <div className="app">
        <Navigation currentGalleryTitle={currentGalleryTitle} />
        <Switch>
          <Route path="/" exact component={Home} />
          <Route path="/galleries" exact component={Galleries} />
          <Route
            path="/gallery/:id/:slug?"
            render={() => <GalleryPage setCurrentGalleryTitle={setCurrentGalleryTitle} />}
          />
          <Route path="/login" component={Login} />
          <Route
            path="/admin/images/:id"
            render={() => (getAuthToken() ? <AdminImageEdit /> : <Redirect to="/login" />)}
          />
          <Route
            path="/admin/galleries/:id"
            render={() => (getAuthToken() ? <AdminGallery /> : <Redirect to="/login" />)}
          />
          <Route
            path="/admin/site-settings"
            render={() => (getAuthToken() ? <AdminSiteSettings /> : <Redirect to="/login" />)}
          />
          <Route
            path="/admin"
            render={() => (getAuthToken() ? <Admin /> : <Redirect to="/login" />)}
          />
        </Switch>
      </div>
    </Router>
  );
};

export default App;